/**
 * PRD.305 §8 — the Labs summary panel in the patient sidebar.
 *
 * A compact, read-first view of the patient's most recent laboratory work,
 * with a link to the full `/patient/:id/labs` page — the same shape
 * `SidebarFilesPanel` and `SidebarPackagesPanel` take.
 *
 * It shows only what the resolved capabilities allow. Under
 * `Published Results Only` (and whenever the Laboratory module is switched
 * off) that means published orders and nothing else, so in-progress laboratory
 * workflow never leaks into a chart that is not meant to see it.
 */
import { useMemo } from 'react';
import {
  qLabOrdersForPatient,
  aggregateLabOrderStatus,
  type LabOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';
import { useLaboratoryCapabilities } from '../hooks/useLaboratory';
import { LabStatusBadge, formatLabDate, parseTestStatuses } from '../pages/laboratory/lab-shared';

const MAX_ROWS = 5;

export function SidebarLabsPanel({
  patientId,
  onViewAll,
}: {
  patientId: string;
  onViewAll: () => void;
}) {
  const capabilities = useLaboratoryCapabilities('patient_workspace');
  const { data: orders = [], loaded } = usePowerSyncQuery<LabOrderRow>(
    qLabOrdersForPatient(patientId),
  );

  const rows = useMemo(() => {
    const mapped = orders.map((o) => ({
      order: o,
      aggregate: aggregateLabOrderStatus(parseTestStatuses(o.test_statuses)),
    }));
    const scoped = capabilities.viewOperational
      ? mapped
      : mapped.filter((r) => r.aggregate === 'published');
    return scoped.slice(0, MAX_ROWS);
  }, [orders, capabilities.viewOperational]);

  if (!capabilities.viewPublished && !capabilities.viewOperational) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {!loaded ? (
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            lineHeight: 1.5,
          }}
        >
          {capabilities.viewOperational
            ? 'No laboratory orders for this patient yet.'
            : 'No published laboratory results yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {rows.map(({ order, aggregate }) => (
            <div
              key={order.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                padding: 'var(--sp-2)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {order.order_number ?? 'Lab order'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {formatLabDate(order.ordered_at)} · {Number(order.test_count ?? 0)} test
                  {Number(order.test_count ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
              <LabStatusBadge status={aggregate} />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onViewAll}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-primary)',
        }}
      >
        View All Labs
      </button>
    </div>
  );
}
